import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  _waitingForConstraints = false;

  connectedCallback() {
    super.connectedCallback();

    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) this.serverDisabledMinus = true;
    if (plusButton.disabled) this.serverDisabledPlus = true;

    this.initialized = true;
    this.updateButtonStates();

    document.addEventListener('change', (e) => {
      if (
        e.target.closest('fieldset') ||
        e.target.name === 'id' ||
        e.target.name?.includes('variant_picker')
      ) {
        this.hideMaxStockMessage();
        const { minusButton, quantityInput } = this.refs;
        minusButton.disabled = true;
        quantityInput.value = quantityInput.min || '1';

        if (!this.serverDisabledPlus) {
          // Activar estado gris via atributo CSS — instantáneo
          this.setAttribute('data-changing', '');
          this._waitingForConstraints = true;
        }
      }
    });

    document.addEventListener('variant:changed', () => this.hideMaxStockMessage());
    document.addEventListener('variantChange', () => this.hideMaxStockMessage());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;
    return { canAdd: !wouldExceedMax, maxQuantity: max, cartQuantity, quantityToAdd };
  }

  getValue() { return this.refs.quantityInput.value; }
  setValue(value) { this.refs.quantityInput.value = value; }

  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));
    if (newValue !== currentValue) quantityInput.value = newValue.toString();

    // Quitar estado de espera y actualizar inmediatamente
    this.removeAttribute('data-changing');
    this._waitingForConstraints = false;
    this.updateButtonStates();
  }

  getCurrentValues() {
    const { quantityInput } = this.refs;
    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    return Math.max(max - cartQuantity, min);
  }

  getMaxStockMessageEl() {
    let node = this.parentElement;
    while (node) {
      const msg = node.querySelector('.quantity-max-message');
      if (msg) return msg;
      if (node.tagName === 'FORM' || node.tagName === 'SECTION' || node.tagName === 'MAIN') break;
      node = node.parentElement;
    }
    const variantId = this.dataset.variantId;
    if (variantId) {
      const wrapper = document.querySelector(`.quantity-selector-wrapper:has([data-variant-id="${variantId}"])`);
      if (wrapper) return wrapper.querySelector('.quantity-max-message');
    }
    return document.querySelector('.quantity-max-message');
  }

  showMaxStockMessage() {
    const msg = this.getMaxStockMessageEl();
    if (!msg) return;
    clearTimeout(this._maxMsgTimeout);
    msg.style.display = 'flex';
    msg.style.opacity = '0';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        msg.style.transition = 'opacity 0.3s ease';
        msg.style.opacity = '1';
      });
    });
    this._maxMsgTimeout = setTimeout(() => {
      msg.style.transition = 'opacity 0.6s ease';
      msg.style.opacity = '0';
      setTimeout(() => { msg.style.display = 'none'; }, 600);
    }, 2000);
  }

  hideMaxStockMessage() {
    const msg = this.getMaxStockMessageEl();
    if (!msg) return;
    clearTimeout(this._maxMsgTimeout);
    msg.style.display = 'none';
    msg.style.opacity = '0';
  }

  updateButtonStates() {
    if (this._waitingForConstraints) return;

    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) minusButton.disabled = value <= min;

    if (!this.serverDisabledPlus) {
      const atMax = effectiveMax !== null && value >= effectiveMax;
      plusButton.setAttribute('aria-disabled', atMax ? 'true' : 'false');
      plusButton.style.opacity = atMax ? '0.4' : '';
      plusButton.disabled = false;
    }
  }

  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));
    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    const effectiveMax = this.getEffectiveMax();
    const { value } = this.getCurrentValues();
    if (effectiveMax !== null && value >= effectiveMax) {
      this.showMaxStockMessage();
      return;
    }
    this.updateQuantity(1);
  }

  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
    quantityInput.select();
  }

  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));
    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }
    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);
    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));
    if (clampedValue !== value) quantityInput.value = clampedValue.toString();
    this.updateButtonStates();
  }

  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }
    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
